import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { usePushNotifications } from "../hooks/usePushNotifications";
import { Bell, BellOff, BellRing } from "lucide-react";
import { toast } from "sonner";

/**
 * Compact button that lets the user enable background push notifications.
 * Shows a colored dot indicator based on current permission state.
 * Designed to sit in the header of any dashboard.
 */
export default function NotificationsActivateButton({ className = "" }) {
  const { user } = useAuth();
  const { supported, permission, subscribed, enable, disable, badgeCount } = usePushNotifications(
    user?.id,
    user?.role
  );
  const [busy, setBusy] = useState(false);

  if (!user || !supported) return null;

  const granted = permission === "granted" && subscribed;
  const blocked = permission === "denied";

  const handleClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (granted) {
        const r = await disable();
        if (r.ok) toast.success("Notifications désactivées");
      } else if (blocked) {
        toast.error(
          "Les notifications sont bloquées par votre navigateur. Activez-les depuis les paramètres du site."
        );
      } else {
        const r = await enable();
        if (r.ok) {
          toast.success("Notifications activées 🔔", {
            description: "Vous recevrez les alertes même lorsque le navigateur est fermé.",
          });
        } else if (r.reason === "denied") {
          toast.error("Permission refusée pour les notifications.");
        } else {
          toast.error("Activation impossible. Réessayez sur un appareil compatible.");
        }
      }
    } finally {
      setBusy(false);
    }
  };

  const Icon = blocked ? BellOff : granted ? BellRing : Bell;
  const stateLabel = blocked
    ? "Bloquées"
    : granted
    ? "Activées"
    : "Activer";
  const helpText = blocked
    ? "Bloquées par le navigateur. Allez dans Paramètres du site > Notifications pour les autoriser."
    : granted
    ? "Notifications de fond actives. Cliquez pour désactiver."
    : "Activer les notifications même quand l'app est fermée";
  const tone = blocked
    ? "bg-red-500/10 text-red-100 border-red-400/30"
    : granted
    ? "bg-emerald-500/15 text-emerald-100 border-emerald-400/40"
    : "bg-[#FACC15]/10 text-[#FACC15] border-[#FACC15]/30 hover:bg-[#FACC15]/20";

  return (
    <button
      onClick={handleClick}
      disabled={busy}
      className={`relative inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-xs font-medium transition-all ${tone} ${className}`}
      data-testid="notifications-activate-btn"
      aria-label={helpText}
      title={helpText}
    >
      <Icon className="w-3.5 h-3.5" />
      <span className="hidden sm:inline">{stateLabel}</span>
      {badgeCount > 0 && (
        <span
          className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-[#EF4444] text-white text-[10px] font-bold rounded-full flex items-center justify-center"
          data-testid="notifications-badge-count"
        >
          {badgeCount > 9 ? "9+" : badgeCount}
        </span>
      )}
    </button>
  );
}
